/**
 * The last view of a list or report page, per team: its remembered fields as a query string
 * (decision 24 a). Written only when the person changes something on the page, so opening a
 * shared link doesn't replace it (decision 26 a). Named periods are stored by name, because the
 * date range codec writes them that way (decision 25 a).
 *
 * Registered as `UI_PREFS.urlLastView`, so sign-out clears it (filters are team data, decision 31 a).
 */

import { prefCodec, prefKey, readPref, UI_PREFS, withCodec, writePref } from '@/lib/ui-prefs';

const LAST_VIEW = withCodec(UI_PREFS.urlLastView, prefCodec.text);

export function lastViewStorageKey(viewId: string, teamId: string): string {
  return prefKey(UI_PREFS.urlLastView, { viewId, teamId }) ?? '';
}

export function loadLastView(viewId: string, teamId: string | null | undefined): string | null {
  return readPref(LAST_VIEW, { viewId, teamId });
}

export function saveLastView(viewId: string, teamId: string | null | undefined, query: string): void {
  // Storage full or blocked: the page still works, it just won't remember. Drop the older view,
  // or it'd come back the moment this change makes the URL bare (choosing All after Draft).
  if (!writePref(LAST_VIEW, query, { viewId, teamId }) && teamId) writePref(LAST_VIEW, null, { viewId, teamId });
}

const VIEW_APPLIED_KEY = 'jfloViewApplied';

/**
 * History state for opening a saved view (`SavedFiltersMenu`): its URL is the whole view, so a
 * view with no filters in it shows no filters, rather than the remembered view put back on top
 * of a bare URL. Carried onto a record opened over it (`useRecordHistory`).
 */
export function viewAppliedState(): Record<string, unknown> {
  return { [VIEW_APPLIED_KEY]: true };
}

/** Whether this history entry was opened as a saved view (`viewAppliedState`). */
export function isViewApplied(state: unknown): boolean {
  return (state as Record<string, unknown> | null)?.[VIEW_APPLIED_KEY] === true;
}

/** The saved-view mark from `state`, to carry onto a new entry; empty when it has none. */
export function carryViewApplied(state: unknown): Record<string, unknown> {
  return isViewApplied(state) ? viewAppliedState() : {};
}
